import type { InterviewSummary } from "@/api/generated/types";
import { Badge } from "@/components/ui/badge";
import { type InterviewDisplayStatus, mapInterviewStatusToDisplay } from "@/lib/interview-ui";
import { cn } from "@/lib/utils";

type InterviewStatus = InterviewSummary["status"];

const statusStyles: Record<InterviewDisplayStatus, string> = {
  面談完了: "bg-teal-50 text-teal-700 border-teal-200",
  面談中: "bg-amber-50 text-amber-700 border-amber-200",
  評価中: "bg-blue-50 text-blue-700 border-blue-200",
  未対応: "bg-slate-100 text-slate-600 border-slate-200",
  新規応募: "bg-blue-50 text-blue-700 border-blue-100",
  評価失敗: "bg-red-50 text-red-600 border-red-100",
  中断: "bg-slate-200 text-slate-700 border-slate-300",
};

const pulseDotStyles: Partial<Record<InterviewDisplayStatus, string>> = {
  面談中: "bg-amber-500",
  評価中: "bg-blue-500",
};

interface InterviewDisplayStatusBadgeProps {
  status: InterviewStatus;
  createdAt: string;
}

export function InterviewDisplayStatusBadge({
  status,
  createdAt,
}: InterviewDisplayStatusBadgeProps) {
  const display = mapInterviewStatusToDisplay(status, createdAt);
  const className = statusStyles[display];

  return (
    <Badge
      variant="outline"
      data-testid="interview-status-badge"
      data-status={display}
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm",
        className
      )}
    >
      {pulseDotStyles[display] && (
        <span
          aria-hidden="true"
          className={cn("w-1 h-1 rounded-full animate-pulse", pulseDotStyles[display])}
        />
      )}
      {display}
    </Badge>
  );
}
